import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { deleteImage, uploadImage } from '../../../../functions/API';
import { SessionData, useSessionStore } from '../../../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { DropdownInput } from '../../../common/baseInputs/DropdownInput';
import { TextInput } from '../../../common/baseInputs/TextInput';
import { Dropdown } from '../../../common/Dropdown';
import { DropdownContentContainer } from '../../../common/DropdownContentContainer';
import { LoadingSpinner } from '../../../common/LoadingSpinner';
import { useScene } from '../../../../context/sceneContext';

const publicAssetsDomain = 'https://editor.patchkit.net/public';

type ImageCategory = {
  label: string;
  images: { fileName: string; catalog: string }[];
};

type IconProperties = {
  color: string;
  weight: string;
};

type IconFilters = {
  color: string | null;
  weight: string | null;
};

export const ImagesPage = () => {
  const scene = useScene();
  const [sessionData, setSessionData] = useSessionStore<SessionData>();
  const [UIData, setUIData] = useUIStore<UIData>();
  const { type } = scene.getSelectedElementsData({ type: undefined }, '');
  const ref = useRef<HTMLInputElement>(null);


  const [isDragOver, setDragOver] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  const [isLoaded, setIsLoaded] = useState(true);
  const [imageData, setImageData] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [isImageDropdownOpen, setIsImageDropdownOpen] = useState(false);
  const [publicAssetsInfo, setPublicAssetsInfo] = useState<{
    images: { fileName: string; catalog: string }[];
  }>({ images: [] });

  const [iconFilters, setIconFilters] = useState<IconFilters>({
    color: 'white',
    weight: 'regular'
  });

  useEffect(() => {
    fetch(publicAssetsDomain + '/info.json')
      .then((response) => response.json())
      .then((data) => {
        setPublicAssetsInfo(data);
      })
      .catch((error) => {
        console.error('Error fetching public assets info:', error);
      });
  }, []);

  useEffect(() => {
    if (imageData) {
      setIsImageDropdownOpen(true);
    } else {
      setIsImageDropdownOpen(false);
    }
  }, [imageData]);

  const onImgClick = (url: string) => {
    if (type === 'img') {
      scene.updateSelectedElementsData(
        { src: url },
        'props',
        'srcVariableInput'
      );
    } else {
      scene.addElement('img', {
        props: { src: url, class: { width: undefined, height: undefined } },
      });
    }
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!sessionData.themeID) return;

    const imgData = e.target.files?.[0];
    if (!imgData) return;
    if (!imgData.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }

    setImageData(imgData);
    setImageName(imgData.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageUrl(event.target?.result as string);
    };
    reader.readAsDataURL(imgData);
  };

  const _uploadImage = async (imageData: File, imageName?: string) => {
    if (!sessionData.themeID) return;

    setImageData(null);
    setImageUrl(null);
    setImageName(null);
    setIsImageDropdownOpen(false);
    setIsLoaded(false);
    ref.current?.value && (ref.current.value = '');

    try {
      const uploadedImg = await uploadImage({
        themeID: sessionData.themeID,
        imageData,
        imageName,
      });

      if (uploadedImg) {
        setSessionData({
          images: [...(sessionData?.images || []), uploadedImg],
        });
      }
      setIsLoaded(true);
    } catch {
      toast.error('Error while uploading the image');
      setIsLoaded(true);
    }
  };

  const _removeImage = async (imgID: string) => {
    if (!sessionData.themeID) return;

    setImageToDelete(imgID);

    try {
      await deleteImage({
        themeID: sessionData.themeID,
        imgID,
      });
      setImageToDelete(null);
      setSessionData({
        images: sessionData.images?.filter((img) => img.id !== imgID),
      });
    } catch {
      toast.error('Error while deleting the image');
      setImageToDelete(null);
    }
  };

  const parseIconProperties = (fileName: string): IconProperties | null => {
    if (!fileName.startsWith('icon-')) return null;
    
    const parts = fileName.split('-');
    let color = 'white';
    let weight = 'regular';
    
    if (parts[1] === 'black' || parts[1] === 'white') {
      color = parts[1];
      if (parts[2] === 'regular' || parts[2] === 'thin') {
        weight = parts[2];
      }
    }
    
    return { color, weight };
  };

  const getUniqueIconProperties = (icons: { fileName: string }[]) => {
    const colors = new Set<string>();
    const weights = new Set<string>();
    
    icons.forEach(icon => {
      const props = parseIconProperties(icon.fileName);
      if (props) {
        colors.add(props.color);
        weights.add(props.weight);
      }
    });
    
    return {
      colors: Array.from(colors),
      weights: Array.from(weights)
    };
  };

  const organizePublicImages = () => {
    const categories: Record<string, ImageCategory> = {};

    publicAssetsInfo.images.forEach(image => {
      const catalog = image.catalog || 'other';
      
      if (!categories[catalog]) {
        const label = catalog
          .split('/')
          .pop()!
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        categories[catalog] = { 
          label, 
          images: [] 
        };
      }

      if (catalog === 'icons') {
        const props = parseIconProperties(image.fileName);
        if (props) {
          const colorMatch = !iconFilters.color || props.color === iconFilters.color;
          const weightMatch = !iconFilters.weight || props.weight === iconFilters.weight;
          
          if (colorMatch && weightMatch) {
            categories[catalog].images.push(image);
          }
        }
      } else {
        categories[catalog].images.push(image);
      }
    });

    return categories;
  };

  return (
    <>
      {sessionData.themeID && (
        <Dropdown
          trigger={
            <UploadItem
              style={{ pointerEvents: isLoaded ? 'auto' : 'none' }}
              $isDragOver={isDragOver}
              onMouseEnter={() => {
                if (imageData) {
                  setIsImageDropdownOpen(true);
                }
              }}
            >
              <input
                ref={ref}
                type="file"
                size={1}
                accept="image/*"
                title=""
                onChange={(e) => onFileChange(e)}
                onDragEnter={() => setDragOver(true)}
                onDragLeave={() => setDragOver(false)}
                onMouseLeave={() => setDragOver(false)}
              />

              {isLoaded ? (
                <span>Drop your file here or browse</span>
              ) : (
                <LoadingSpinner size={16} color="var(--main-2)" />
              )}
            </UploadItem>
          }
          isOpen={isImageDropdownOpen}
          setOpen={setIsImageDropdownOpen}
          horizontalAlign="center"
          contentStyle={{ width: '100%' }}
          closeOnClick={false}
          ignoreTriggerClick={true}
        >
          <DropdownContentContainer style={{ margin: '10px', gap: '5px' }}>
            <Image>{imageUrl && <img src={imageUrl} />}</Image>
            <TextInput
              label="Name"
              value={imageName || undefined}
              onChange={(value) => setImageName(value || null)}
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SubmitButton
                onClick={() =>
                  imageData && _uploadImage(imageData, imageName || undefined)
                }
              >
                Add
              </SubmitButton>
            </div>
          </DropdownContentContainer>
        </Dropdown>
      )}

      <ScrollContainer className="scroll">
        <Grid>
          {sessionData.images
            ?.filter((img) => img.tag !== 'version_thumbnail')
            ?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            ?.map((img, i) => (
              <Item
                key={i}
                onClick={() => onImgClick(img.url)}
                style={
                  imageToDelete === img.id
                    ? { opacity: 0.5, pointerEvents: 'none' }
                    : undefined
                }
              >
                <img src={img.url} />
                <div className="data">
                  <span>{img.name}</span>
                </div>
                {imageToDelete === null && (
                  <div
                    className="removeButton"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!sessionData.themeID) return;

                      setUIData({
                        isDialogOpen: true,
                        dialogData: {
                          label: 'Are you sure you want to delete this image?',
                          children: (
                            <>
                              <img src={img.url} />
                              <p>{img.name}</p>
                            </>
                          ),
                          options: [
                            {
                              text: 'Cancel',
                              onClick: () => {
                                setUIData({
                                  isDialogOpen: false,
                                });
                              },
                            },
                            {
                              text: 'Delete',
                              onClick: () => {
                                _removeImage(img.id);
                                setUIData({
                                  isDialogOpen: false,
                                });
                              },
                              type: 'danger',
                            },
                          ],
                          onClose: () =>
                            setUIData({
                              isDialogOpen: false,
                            }),
                        },
                      });
                    }}
                  >
                    <i className="icon-x" />
                  </div>
                )}
              </Item>
            ))}
        </Grid>

        {Object.entries(organizePublicImages()).map(([key, category]) => 
          category.images.length > 0 && (
            <div key={key}>
              <Label
                style={{
                  position: 'sticky',
                  top: '0',
                  backgroundColor: 'var(--neutral-2)',
                  zIndex: 1,
                  padding: '10px 0'
                }}
              >
                <div className="titleRow">
                  <Separator />
                  {category.label}
                  <Separator />
                </div>
                {key === 'icons' && (
                  <FiltersContainer>
                    <DropdownInput
                      value={iconFilters.color || ''}
                      onChange={(value) => setIconFilters(prev => ({
                        ...prev,
                        color: value as string || null
                      }))}
                      options={getUniqueIconProperties(publicAssetsInfo.images).colors.map(color => ({
                        value: color,
                        label: color.charAt(0).toUpperCase() + color.slice(1)
                      }))}
                      placeholder="All Colors"
                      isSearchable={false}
                    />
                    <DropdownInput
                      value={iconFilters.weight || ''}
                      onChange={(value) => setIconFilters(prev => ({
                        ...prev,
                        weight: value as string || null
                      }))}
                      options={getUniqueIconProperties(publicAssetsInfo.images).weights.map(weight => ({
                        value: weight,
                        label: weight.charAt(0).toUpperCase() + weight.slice(1)
                      }))}
                      placeholder="All Weights"
                      isSearchable={false}
                    />
                  </FiltersContainer>
                )}
              </Label>

              <Grid>
                {category.images.map(({ fileName, catalog }, i) => {
                  const url = `${publicAssetsDomain}/${catalog ? catalog + '/' : ''}${fileName}`;
                  return (
                    <Item key={i} onClick={() => onImgClick(url)}>
                      <img src={url} />
                      <div className="data">
                        <span>{fileName}</span>
                      </div>
                    </Item>
                  );
                })}
              </Grid>
            </div>
          )
        )}
      </ScrollContainer>
    </>
  );
};

const ScrollContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
`;

const Image = styled.div`
  display: flex;
  justify-content: center;

  & > img {
    user-select: none;
    max-width: 100%;
    object-fit: contain;
  }
`;

const UploadItem = styled.div<{ $isDragOver?: boolean }>`
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  padding: var(--padding-1);
  margin: var(--padding-1);
  margin-bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;

  border: 1px dashed var(--neutral-3);
  border-radius: 10px;

  transition:
    color 0.3s ease,
    border 0.3s ease;

  &:hover {
    border-color: var(--main-1);
    color: var(--main-1);
  }

  & > input {
    cursor: pointer;
    position: absolute;
    width: 100%;
    height: 100%;
    font-size: 0;
    opacity: 0;
  }

  ${({ $isDragOver }) =>
    $isDragOver &&
    `
      background-color: var(--neutral-3);
      border-color: var(--main-1);
      color: var(--main-1);
    `}
`;

const SubmitButton = styled.div`
  box-sizing: border-box;
  cursor: pointer;

  width: 100px;
  padding: 5px;
  display: flex;
  justify-content: center;

  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-1);

  color: var(--main-2);

  transition:
    color 0.3s ease,
    border 0.3s ease;
  &:hover {
    color: var(--main-1);
    border: 1px solid var(--main-1);
  }
`;

const Grid = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: var(--padding-1);
  display: flex;
  flex-wrap: wrap;
  gap: var(--padding-1);

  animation: slideLeft 1s cubic-bezier(0.075, 0.82, 0.165, 1);
`;

const Item = styled.div`
  cursor: pointer;
  position: relative;
  flex: 25%;
  max-width: 250px;
  min-width: 100px;
  aspect-ratio: 1;

  outline: 1px solid transparent;
  border-radius: var(--radius-2);
  overflow: hidden;

  display: flex;
  justify-content: center;
  align-items: center;

  & > img {
    user-select: none;
    max-height: 100%;
    object-fit: contain;
  }

  transition: outline-color 0.3s ease;

  &:hover {
    outline-color: var(--neutral-3);
  }

  &:hover > .data {
    opacity: 1;
  }
  .data {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.3s ease;

    & > span {
      pointer-events: none;
      box-sizing: border-box;
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      padding: 2px 5px;

      background-color: var(--neutral-5);
      color: #eee;
      text-align: center;
      font-size: 10px;

      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &:hover > .removeButton {
    opacity: 1;
  }
  .removeButton {
    transition:
      color 0.3s ease,
      opacity 0.3s ease;

    opacity: 0;
    background-color: var(--panel-bg-color);
    border-radius: 5px;

    position: absolute;
    top: 0;
    right: 0;
    padding: 5px;
    margin: 5px;

    &:hover {
      background-color: var(--neutral-1);
    }
  }
`;

const Label = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: 5px 0;
  font-size: 11px;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .titleRow {
    display: flex;
    align-items: center;
    gap: 15px;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  margin: 5px 0;

  background-color: var(--neutral-3);
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 var(--padding-1);
`;
